<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from '@vue/composition-api';
import { usePromise } from 'vue-composable';
import { useIsOpen } from '@/_core/utils/use/useIsOpen';
import { useNavDrawer } from './use';

import { useCurrentUserModule } from '@/store/global/currentUser';
import { useCurrentWsStore, InitCurrentWs } from '@/store/main/currentWs';
import { InitCurrentUser } from '@/store/global/currentUser/actions/initCurrentUser';
import * as notificationsModule from '@/store/global/notifications';

import UserProfile from '@/pages/components/userProfile/UserProfile.vue';
import NotificationSettings from '@/pages/components/notification-settings/NotificationSettings.vue';
import NavDrawer from '@/pages/components/NavDrawer.vue';
import SlidePanelContainer from '@/pages/components/SlidePanelContainer.vue';
import AppBar from '@/pages/components/AppBar.vue';
import PublishedRecipientAppBar from '@/pages/components/PublishedRecipientAppBar.vue';
import { trackingService } from './_core/tracking/trackingService';
import { TrackedPage } from './_core/tracking/trackedpages';
import ApiToken from '@/pages/components/api-token/ApiToken.vue';
import NotificationsPanel from '@/pages/components/notifications/NotificationsPanel.vue';

import intercomService from '@/_core/utils/intercom';

import * as productsStoreModule from '@/store/main/Products';

function useInitData(autoStart: boolean) {
    const currentUserStore = useCurrentUserModule();

    const { loading, exec: initStore } = usePromise(() => currentUserStore.dispatch(new InitCurrentUser()), true);
    if (autoStart) {
        initStore();
    }

    return { loading, initStore };
}

function useUserProfile() {
    const { isOpen: isUserProfileOpen, open, close: closeUserProfile } = useIsOpen();
    const openUserProfile = () => {
        trackingService.TrackSimplePageVisit(TrackedPage.UserProfile);
        open();
    };
    return {
        isUserProfileOpen,
        openUserProfile,
        closeUserProfile,
    };
}

function useNotificationSettings() {
    const { isOpen: isNotificationSettingsOpen, open, close: closeNotificationSettings } = useIsOpen();

    const openNotificationSettings = () => {
        open();
        trackingService.TrackSimplePageVisit(TrackedPage.UserNotificationSettings);
    };

    return {
        isNotificationSettingsOpen,
        openNotificationSettings,
        closeNotificationSettings,
    };
}

export default defineComponent({
    name: 'App',
    components: {
        UserProfile,
        NavDrawer,
        SlidePanelContainer,
        AppBar,
        PublishedRecipientAppBar,
        NotificationSettings,
        ApiToken,
        NotificationsPanel,
    },

    // eslint-disable-next-line
    setup(props, ctx) {
        const reducedRecipientShellMode = 1;
        const isPublishedRecipientShell = computed(
            () => initAppData.publishedStorymapBootstrap?.shellMode === reducedRecipientShellMode
        );
        const shouldInitializeNormalShell = computed(() => !isPublishedRecipientShell.value);

        const { loading, initStore } = useInitData(false);
        const isNormalShellInitialized = ref(false);
        const initializeNormalShell = () => {
            if (isNormalShellInitialized.value) {
                return;
            }

            isNormalShellInitialized.value = true;
            useCurrentWsStore().dispatch(new InitCurrentWs());
            productsStoreModule.useProductsStore().dispatch(new productsStoreModule.InitProducts());
            initStore();
        };
        watch(shouldInitializeNormalShell, (shouldInitialize) => {
            if (shouldInitialize) {
                initializeNormalShell();
            }
        }, { immediate: true });

        const notificationsStore = notificationsModule.useNotificationsStore();

        const { isOpen: isApiTokenDialogOpen, open: openApiTokenDialog, close: closeApiTokenDialog } = useIsOpen();

        watch(loading, () => {
            if (!isPublishedRecipientShell.value) {
                intercomService.boot(ctx.root.$vuetify.breakpoint.xsOnly);
            }
        });

        const state = reactive({
            numberOfNonDismissedNotifications: initAppData.isInWsContext
                ? initAppData.currentWs.numberOfNonDismissedNotifications
                : initAppData.numberOfNonDismissedNotifications,
        });

        const notificationsPanel = {
            isNotificationsPanelOpen: ref(false),
            onNotificationsPanelToggle: () => {
                notificationsPanel.isNotificationsPanelOpen.value = !notificationsPanel.isNotificationsPanelOpen.value;
            },
            onNotificationsPanelClose: () => {
                notificationsPanel.isNotificationsPanelOpen.value = false;
            },
            notificationsCount: computed(() => {
                return !notificationsStore.state.isInitialized
                    ? state.numberOfNonDismissedNotifications
                    : notificationsStore.state.newNotifications.length;
            }),
        };

        return {
            ...useNavDrawer(ctx),
            ...useUserProfile(),
            ...useNotificationSettings(),
            ...notificationsPanel,

            loading,

            isApiTokenDialogOpen,
            openApiTokenDialog,
            closeApiTokenDialog,
            isPublishedRecipientShell,
        };
    },
});
</script>

<template>
  <v-app>
    <NavDrawer
      v-if="!isPublishedRecipientShell"
      :isOpen="drawer"
      :storedDrawer="storedDrawer"
      @openChanged="toggleNavDrawer"
      @openUserProfile="openUserProfile"
      @openNotificationSettings="openNotificationSettings"
      @openApiTokenDialog="openApiTokenDialog"
    />

    <SlidePanelContainer v-if="!isPublishedRecipientShell" />

    <AppBar
      v-if="!isPublishedRecipientShell"
      :navDrawer="drawer"
      :isNotificationsPanelOpen="isNotificationsPanelOpen"
      :notificationsCount="notificationsCount"
      @notificationsPanelToggle="onNotificationsPanelToggle"
      @openUserProfile="openUserProfile"
      @openNavDrawer="toggleNavDrawer"
    />
    <PublishedRecipientAppBar v-if="isPublishedRecipientShell" />

    <v-main class="surface blueGrey05Light" style="height: 100vh;">
      <v-divider />
      <!-- <AppPageLoader :isLoading="loading" delay="300">  -->
      <v-slide-y-transition mode="out-in">
        <router-view class="full-height" style="overflow-y: scroll;" @openApiTokenDialog="openApiTokenDialog" />
      </v-slide-y-transition>
      <!-- </AppPageLoader> -->
    </v-main>

    <template v-if="!loading">
      <AppSnackbar />

      <AppConfirm />

      <AppLoader />

      <AppCsrfTokenDialog />

      <UserProfile v-if="!isPublishedRecipientShell" :isOpen="isUserProfileOpen" @close="closeUserProfile" />
      <NotificationSettings v-if="!isPublishedRecipientShell" :isOpen="isNotificationSettingsOpen" @close="closeNotificationSettings" />

      <ApiToken v-if="!isPublishedRecipientShell" :isOpen="isApiTokenDialogOpen" @close="closeApiTokenDialog" />

      <NotificationsPanel v-if="!isPublishedRecipientShell" :isOpen="isNotificationsPanelOpen" @close="onNotificationsPanelClose" />
    </template>
  </v-app>
</template>

<style lang="scss">
@import '../node_modules/@syncfusion/ej2-base/styles/material.css';
@import '../node_modules/@syncfusion/ej2-buttons/styles/material.css';
@import '../node_modules/@syncfusion/ej2-calendars/styles/material.css';
@import '../node_modules/@syncfusion/ej2-dropdowns/styles/material.css';
@import '../node_modules/@syncfusion/ej2-inputs/styles/material.css';
@import '../node_modules/@syncfusion/ej2-navigations/styles/material.css';
@import '../node_modules/@syncfusion/ej2-lists/styles/material.css';
@import '../node_modules/@syncfusion/ej2-layouts/styles/material.css';
@import '../node_modules/@syncfusion/ej2-popups/styles/material.css';
@import '../node_modules/@syncfusion/ej2-splitbuttons/styles/material.css';
@import '../node_modules/@syncfusion/ej2-grids/styles/material.css';
@import '../node_modules/@syncfusion/ej2-richtexteditor/styles/material.css';
@import '../node_modules/@syncfusion/ej2-treegrid/styles/material.css';
@import '../node_modules/@syncfusion/ej2-vue-gantt/styles/material.css';

* {
    &::-webkit-scrollbar {
        width: 12px;
        height: 12px;
    }

    &::-webkit-scrollbar-track {
        background: #f0f0f0;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #c1c1c1;
    }

    & {
        scrollbar-width: auto;
    }
}

/* production vuetify has all margins set to -12px (on local only margin-left and margin-right set to -12px) */
.row {
    margin: 0 -12px 0 -12px;
}

ol.prominent-list li {
    margin-top: 12px;

    &:first-of-type {
        margin-top: 0;
    }
}

.full-height {
    height: 100% !important;
}

.height-auto {
    height: auto !important;
}

.break-word {
    word-break: break-word !important;
}

.ellipsis {
    text-overflow: ellipsis;

    /* Required for text-overflow to do anything */
    white-space: nowrap;
    overflow: hidden;
}

.line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.label {
    font-size: 14px;
    font-weight: 600;
}

.font-weight-semibold {
    font-weight: 600;
}

:focus {
    outline: none;
}

.loader-caret {
    display: inline-block;
    width: 1px;
    height: 14px;
    background-color: var(--v-blueGrey80Light-base) !important;
    animation: loader-caret-blinking 1s step-start 0s infinite;
}

@keyframes loader-caret-blinking {
    50% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

// Default (was missing in production)
.v-dialog > .v-card > .v-card__actions {
    padding: 8px 16px;
}

.v-card > .v-card__title {
    word-break: normal;
    overflow-wrap: anywhere;
}

.theme--light {
    &.v-application {
        font-family: $body-font-family, sans-serif !important;
        // color: var(--v-secondaryText-base) !important;

        // .elevation-8 {
        //     box-shadow: 0px 3px 3px 1px red, 0px 3px 5px 0px red, 0px 1px 11px 1px red !important;
        // }

        .v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
            background-color: #dde2ef !important;
        }

        .v-text-field--solo.error--text {
            .v-input__slot {
                border: 1px solid var(--v-error-base) !important;
            }
        }

        .text-area-14 textarea {
            font-size: 14px !important;
            line-height: 22px !important;
        }

        .v-menu__content {
            // shadow 60
            box-shadow: 0 8px 22px -8px rgba(31, 40, 72, 0.34) !important;
            border: 1px solid var(--v-blueGrey30Light-base) !important;
            max-height: 98%;

            .v-sheet.v-card:not(.v-sheet--outlined) {
                border: none !important;
            }

            .v-card--link:focus:before {
                display: none !important;
            }
        }

        .v-navigation-drawer {
            // shadow 40
            box-shadow: 0 6px 12px -6px rgba(31, 40, 72, 0.34) !important;
        }

        .v-data-table {
            border: 1px solid var(--v-blueGrey30Light-base);
            &.elevation-1 {
                box-shadow: 0 3px 7px -4px rgba(31, 40, 72, 0.25) !important;
            }

            tbody tr:hover {
                background-color: rgb(207 219 255 / 0.3) !important;
            }
        }

        .v-data-table__wrapper > table > thead > tr:last-child > th {
            border-bottom: 1px solid var(--v-blueGrey30Light-base);
        }

        .v-data-table .v-data-footer {
            margin-right: 60px;
        }

        hr,
        .v-navigation-drawer__border {
            background-color: var(--v-blueGrey30Light-base) !important;
        }

        .secondary--text {
            color: var(--v-secondaryText-base);
        }

        .title,
        .headline {
            // To pin point specific classes of some components
            font-family: $body-font-family, sans-serif !important;
        }

        .v-input__icon.v-input__icon--prepend-inner > span.v-icon--svg {
            width: 16px;
            height: 16px;
        }

        .v-data-footer {
            padding: 0.5rem;

            .v-data-footer__pagination {
                margin: 0 20px;
            }
            // .v-data-footer__icons-before,
            // .v-data-footer__icons-after {
            //     width: 16px;
            // }

            button {
                width: 24px;
                height: 24px;

                margin: 0 !important;

                &:hover {
                    .v-icon--svg path {
                        fill: var(--v-blue50Light-base);
                    }
                }
            }
        }

        .v-tab {
            &:before {
                transition: none;
            }

            text-transform: none;
        }

        .v-alert__icon {
            border-radius: 0;
        }

        p {
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

button,
html [type='button'],
[type='reset'],
[type='submit'] {
    -webkit-appearance: none;
    /* Correct the inability to style clickable types in iOS */
}

html {
    overflow-y: hidden;
}

a {
    text-decoration: none;
}

#consent-container {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    z-index: 10000;
}

// .v-icon--svg {
//     svg {
//         path {
//             fill: var(--v-blueGrey90Light-base);
//         }
//     }
// }

.v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
    background-color: rgb(207 219 255 / 0.3);
    opacity: 1;
}

.v-btn:not(.v-btn--text):not(.v-btn--outlined):not(.primary):not(.pink50Light):hover {
    svg path {
        fill: var(--v-blue50Light-base) !important;
    }
}

.v-btn:not(.v-btn--text):not(.v-btn--outlined):before {
    background-color: transparent;
}

.v-btn:not(.v-btn--text):not(.v-btn--outlined).v-btn--active {
    color: var(--v-blue50Light-base);
    background-color: rgb(207 219 255 / 0.3);
}

.v-date-picker-table .v-btn:not(.v-btn--text):not(.v-btn--outlined).v-btn--active {
    color: white;
}

.v-list-item--dense .v-list-item__icon,
.v-list--dense .v-list-item .v-list-item__icon {
    min-width: 16px;

    .v-icon--svg,
    svg {
        font-size: 16px;
        height: 16px;
        width: 16px;

        svg {
            width: 16px;
            height: 16px;
        }
    }
}

.v-list-item--link {
    &.v-list-item--active {
        &:before {
            // opacity: 0;
            background-color: var(--v-blue50Light-base);
        }

        &:after {
            position: absolute;
            left: -8px;
            height: 100%;
            width: 2px;
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
            background: var(--v-blue50Light-base);
        }

        .v-list-item__title {
            color: var(--v-blue50Light-base) !important;
        }

        svg path {
            fill: var(--v-blue50Light-base) !important;
        }
    }

    &:hover,
    &:focus {
        border-radius: 4px;
        background-color: rgb(207 219 255 / 0.3);

        .v-list-item__title {
            color: var(--v-blue50Light-base) !important;
        }

        svg path {
            fill: var(--v-blue50Light-base) !important;
        }

        &:before {
            // position: absolute;
            // left: -16px;
            // height: 100%;
            // width: 2px;
            // border-top-right-radius: 10px;
            // border-bottom-right-radius: 10px;
            opacity: 0.3;
            background-color: var(--v-blue10Light-base);
        }
    }
}

.v-select > .v-input__control > .v-input__slot {
    cursor: pointer !important;
}

.small-input {
    > .v-input__control {
        min-height: 30px !important;
    }
}

.CodeMirror-hints {
    font-family: $body-font-family, sans-serif !important;
    position: absolute;
    z-index: 2000;
    overflow: hidden;
    list-style: none;
    margin: 0;
    -webkit-box-shadow: 2px 3px 5px rgb(0 0 0 / 20%);
    -moz-box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.2);
    box-shadow: 2px 3px 5px rgb(0 0 0 / 20%);
    background: white;
    max-height: 20em;
    overflow-y: auto;
    -webkit-box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 3px 6px rgb(0 0 0 / 23%);
    -moz-box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
    box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 3px 6px rgb(0 0 0 / 23%);
    min-width: 180px;
    background-color: #fff;
    border: 1px solid #cfcfcf;
    border-radius: 2px;
    padding: 5px;
}

li.CodeMirror-hint-active {
    background-color: #f0f3ff;
    color: var(--v-blue50Light-base) !important;
    border-radius: 4px;
}

.CodeMirror-hint {
    margin: 0;
    opacity: 0.9;
    padding: 5px 10px;
    border-radius: 2px;
    white-space: pre;
    color: #143759;
    cursor: pointer;
}

.CodeMirror-hint.type-for-more-hint {
    cursor: default;
    font-size: 12px;
    opacity: 0.9;
    color: #143759;
    padding: 15px 6px 6px 10px;
    text-align: left;
    white-space: nowrap;
    position: relative;

    &:before {
        content: '';
        position: absolute;
        height: 1px;
        background: #f3f3f3;
        top: 5px;
        left: 6px;
        right: 6px;
    }
}

// Syncfusion
.e-grid {
    font-family: 'Inter', sans-serif, sans-serif !important;
}

.e-splitter.e-splitter-horizontal .e-pane,
.e-splitter.e-splitter-vertical .e-pane {
    font-family: 'Inter', sans-serif, sans-serif !important;
}

.e-tooltip-wrap .e-tip-content {
    font-family: 'Inter', sans-serif, sans-serif !important;
}

.e-grid .e-rowcell:not(.e-editedbatchcell):not(.e-updatedtd),
.e-grid .e-detailrowcollapse:not(.e-editedbatchcell):not(.e-updatedtd),
.e-grid .e-detailrowexpand:not(.e-editedbatchcell):not(.e-updatedtd),
.e-grid .e-gridcontent .e-rowdragdrop:not(.e-editedbatchcell):not(.e-updatedtd),
.e-grid .e-gridheader .e-rowdragdrop:not(.e-editedbatchcell):not(.e-updatedtd),
.e-grid .e-emptyrow:not(.e-editedbatchcell):not(.e-updatedtd) .mention {
    color: var(--v-blueGrey80Light-base) !important;
}

.e-gantt .e-gantt-chart .e-timeline-top-header-cell,
.e-gantt .e-gantt-chart .e-timeline-single-header-cell {
    color: var(--v-blueGrey80Light-base) !important;
}

.e-gantt .e-gantt-chart .e-gantt-child-taskbar-inner-div {
    background: var(--v-primary-base) !important;
    outline: none;
    border-radius: 4px;
}

.e-gantt .e-gantt-chart .e-gantt-child-progressbar-inner-div {
    background: var(--v-primary-base) !important;
}

.e-gantt .e-gantt-chart .e-gantt-milestone {
    background: var(--v-primary-base) !important;
    border-color: var(--v-primary-base) !important;
}

.e-gantt .e-gantt-chart .e-active {
    background: var(--v-secondaryButton-base) !important;
}

.e-gantt .e-gantt-tree-grid-pane .e-grid td.e-active {
    background: var(--v-secondaryButton-base) !important;
}

.e-grid .e-headercell,
.e-grid .e-detailheadercell {
    background: var(--v-blueGrey05Light-base) !important;
}

.e-gantt .e-gantt-chart .e-weekend-header-cell {
    background: fff !important;
}

.e-grid .e-table {
    background: var(--v-blueGrey05Light-base) !important;
}

.e-gantt .e-gantt-chart .e-timeline-header-container {
    background: var(--v-blueGrey05Light-base) !important;
}

.e-gantt .e-gantt-chart .e-chart-root-container {
    background-color: var(--v-blueGrey05Light-base) !important;
}

.e-grid .e-content {
    background-color: var(--v-blueGrey05Light-base) !important;
}

.e-splitter.e-splitter-horizontal,
.e-splitter.e-splitter-vertical {
    border: none;
}

.e-treegrid .e-rowcell:not(.e-gridclip) .e-treecolumn-container {
    text-overflow: clip;
    display: flex;
    align-items: center;

    span.e-treecell {
        display: inline-block;
        width: 100%;
    }

    .e-none {
        width: 0 !important;
    }
}

.pointer,
.cursor-pointer {
    cursor: pointer !important;
}

.cursor-default {
    cursor: default;
}

.img-responsive {
    max-width: 100%;
}

.thin-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.thin-scrollbar::-webkit-scrollbar-track {
    background: #f0f0f0;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
}

.thin-scrollbar {
    scrollbar-width: thin;
}

.viewer-container .viewer-title {
    font-family: $body-font-family, sans-serif !important;
}

.v-application {
    .body-2,
    .v-card__subtitle,
    .v-card__text {
        letter-spacing: normal !important;
    }
}

.keyboard-shortcut-container {
    border-radius: 4px;
    font-size: 12px;
    padding: 4px 8px;
    background-color: var(--v-blueGrey70Light-lighten3);
    color: var(--v-blueGrey80Light-base);
}

.keyboard-shortcut {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;
    padding: 2px 6px;
    line-height: 12px;
    font-weight: 500;
    border-radius: 4px;
    background: white;
    color: var(--v-blueGrey80Light-base) !important;
    text-transform: uppercase;
}
</style>
